// Feed.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button
} from "reactstrap";
import { useInView } from 'react-intersection-observer';
import ReactMarkdown from 'react-markdown';
import useFeed from '../../../hooks/useFeed';

import { UserSearch } from 'lucide-react';

import axiosInstance from 'utils/axiosInstance';

import './Feed.css';

const PostCard = ({ post, liked, setLikedMap }) => {
  const [localLikeCount, setLocalLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(liked);

  useEffect(() => {
    setIsLiked(liked);
  }, [liked]);

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });

  useEffect(() => {
    if (inView) {
      axiosInstance.post('/knovia/myInfo/events', { postId: post._id, type: 'view' })
        .catch(console.error);
    }
  }, [inView]);

  
  const handleLikeToggle = async () => {
    try {
      if (isLiked) {
        await axiosInstance.delete('/knovia/myInfo/events', {
          data: { postId: post._id, type: 'like' }
        });
        setLocalLikeCount(c => c - 1);
        setLikedMap(prev => ({ ...prev, [post._id]: false }));
      } else {
        await axiosInstance.post('/knovia/myInfo/events', { postId: post._id, type: 'like' });
        setLocalLikeCount(c => c + 1);
        setLikedMap(prev => ({ ...prev, [post._id]: true }));
      }
      setIsLiked(!isLiked);
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  return (
    <div ref={ref} className="mb-4 pb-4 border-bottom">
      {post.topics?.length > 0 && (
        <div className="mb-2">
          {post.topics.map((topic, i) => (
            <span
              key={i}
              className="badge bg-secondary me-1"
              style={{ fontSize: '0.75rem' }}
            >
              #{topic}
            </span>
          ))}
        </div>
      )}

      <h4>@{post.author}</h4>

      {post.images?.length > 0 && (
        <Row className="mb-3">
          {post.images.map((url, i) => (
            <Col md={4} sm={6} xs={12} key={i} className="mb-2">
              <img src={url} className="img-fluid rounded" alt="" loading="lazy" />
            </Col>
          ))}
        </Row>
      )}

      <div className="mb-2">
        {post.format === 'md'
          ? <ReactMarkdown>{post.text}</ReactMarkdown>
          : <p>{post.text}</p>}
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <span className="text-muted small">
          {new Date(post.createdAt).toLocaleString()}
        </span>
        <div className="d-flex align-items-center">
          <span className="d-flex me-3 text-muted">
            <UserSearch className="me-1" size={16} /> 
            <p className="mb-0">{post.views ?? 0}</p>
          </span>
          <Button 
            color={isLiked ? 'danger' : 'outline-danger'}
            size="sm" 
            onClick={handleLikeToggle}
          >
            ❤️ {(post.likes ?? 0) + localLikeCount}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Feed = () => {

  const [likedMap, setLikedMap] = useState({});

  const availableTopics = ['tech', 'music', 'gaming', 'design', 'news'];
  const [selectedTopics, setSelectedTopics] = useState([]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    status,
    isFetchingNextPage,
    isFetching,
  } = useFeed(selectedTopics);
  const { ref, inView } = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage]);

  const posts = useMemo(() => {
    return data?.pages.flatMap(p => p.items) ?? [];
  }, [data]);

  useEffect(() => {
    
    const fetchLikedMap = async () => {
      const postIds = posts.map(p => p._id).join(',');
      if (!postIds) return;
      try {
        const likedResponse = await axiosInstance.post('/knovia/myInfo/events/liked-posts', 
          { ids: postIds }
        );
        console.log('likedResponse.data:', likedResponse.data);
        const map = {};
        likedResponse.data.likedPostIds.forEach(id => { map[id] = true });
        setLikedMap(map);
      } catch (err) {
        console.error('Failed to fetch liked posts', err);
      }
    }
   
    console.log("Post IDs loaded:", posts.map(p => p._id));
    fetchLikedMap();
  }, [posts]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>

              <div>
                {availableTopics.map(topic => (
                  <Button
                    key={topic}
                    disabled={status === 'loading'}
                    color={selectedTopics.includes(topic) ? 'primary' : 'outline-primary'}
                    size="sm"
                    className="me-2 mb-2"
                    onClick={() => {
                      setSelectedTopics(prev =>
                        prev.includes(topic)
                          ? prev.filter(t => t !== topic)
                          : [...prev, topic]
                      );
                    }}
                  >
                    #{topic}
                  </Button>
                ))}
              </div>
              
              <div className="text-muted mt-1 mb-3" style={{ minHeight: "20px" }}>
                {isFetching && !isFetchingNextPage ? (
                  <p className="mb-0">Fetching new feed…</p>
                ) : (
                  <p className="mb-0">Feed Loaded…</p>
                )}
              </div>
              

              {posts.map(p => (
                <PostCard 
                  key={p._id} 
                  post={p} 
                  liked={!!likedMap[p._id]} 
                  setLikedMap={setLikedMap} 
                />
              ))}

              <div ref={ref} className="py-6 text-center">
                {isFetchingNextPage
                  ? 'Loading…'
                  : !hasNextPage
                    ? '— End —'
                    : null}
              </div>

            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default Feed;



